const SECOND_MS = 1_000;
const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;

/**
 * Compact wall-clock formatter for millisecond durations.
 *
 * - `< 1m`  → fractional seconds, e.g. `"0.4s"`, `"4.2s"`
 * - `< 1h`  → minutes + seconds, dropping seconds when zero — `"1m 23s"` / `"5m"`
 * - `>= 1h` → hours + minutes, dropping minutes when zero — `"2h 15m"` / `"3h"`
 *
 * Returns `"0.0s"` for negative, NaN, or non-finite inputs.
 *
 * Units stay in English (`s`/`m`/`h`) on every locale; embed the result in
 * a localized template via `t('key', { duration: formatDuration(ms) })`.
 */
export function formatDuration(durationMs: number): string {
  if (!Number.isFinite(durationMs) || durationMs < 0) {
    return '0.0s';
  }

  if (durationMs < MINUTE_MS) {
    // Sub-10s values show fractional precision ("4.2s") because OCR-style
    // durations live in that band and the tenths matter. From 10s up to a
    // minute, drop the decimal so countdowns / longer scans read cleanly
    // ("45s" rather than "45.0s").
    if (durationMs < 10 * SECOND_MS) {
      return `${(durationMs / SECOND_MS).toFixed(1)}s`;
    }
    return `${Math.round(durationMs / SECOND_MS)}s`;
  }

  if (durationMs < HOUR_MS) {
    const minutes = Math.floor(durationMs / MINUTE_MS);
    const seconds = Math.round((durationMs - minutes * MINUTE_MS) / SECOND_MS);
    return seconds === 0 ? `${minutes}m` : `${minutes}m ${seconds}s`;
  }

  const hours = Math.floor(durationMs / HOUR_MS);
  const minutes = Math.round((durationMs - hours * HOUR_MS) / MINUTE_MS);
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
}
